import { gApp } from '../Globals.js'
import { Units } from '../Common/Units.js'
import { Quantity } from '../Common/Quantity.js'
import { combineTableData, formatColumnHeader, formatCellValue } from '../Common/TableData.js'
import { workbookToCsv } from '../SharedUtils.js'

function getComponentSections(system) {
  const types = system.getTypesSet();
  return [
    { title: 'Walls', items: [...types.wallTypes] },
    { title: 'Roofs', items: [...types.roofTypes] },
    { title: 'Windows', items: [...types.windowTypes] },
    { title: 'Doors', items: [...types.doorTypes] },
    { title: 'Skylights', items: [...types.skylightTypes] },
  ];
}

function addTableDataToSheet(worksheet, exporter, tableData, baseTableName) {
  const tableStartRow = worksheet.rowCount + 1;
  const startCol = 2; // Column B, matching the convention used elsewhere
  const refCellAddress = worksheet.getRow(tableStartRow).getCell(startCol).address;

  worksheet.addTable({
    name: exporter.makeTableName(baseTableName),
    ref: refCellAddress,
    headerRow: true,
    style: { showRowStripes: true },
    columns: tableData.columns.map(col => ({ name: formatColumnHeader(col) })),
    rows: tableData.rows.map(row => row.map(formatCellValue)),
  });

  // Apply per-cell bold formatting where requested by the source data
  tableData.rows.forEach((row, rowIdx) => {
    row.forEach((cell, colIdx) => {
      if (cell.isBold) {
        const excelRow = worksheet.getRow(tableStartRow + 1 + rowIdx);
        excelRow.getCell(startCol + colIdx).font = { bold: true };
      }
    });
  });

  worksheet.addRow([]); // Spacer after the table
}

/**
 * Generates a Component Tables worksheet with Walls, Roofs, Windows, Doors, and Skylights
 * @param {Object} worksheet - ExcelJS worksheet object
 * @param {Object} exporter - SpreadsheetExporterExcel instance (for unique table names)
 */
export function generateComponentTablesWorksheet(worksheet, exporter, system) {
  if (!gApp.proj()) return;

  for (const section of getComponentSections(system)) {
    const combined = combineTableData(section.items.map(item => item.getComponentTableData()));
    if (!combined) continue;
    const titleRow = worksheet.addRow(['', section.title]);
    titleRow.font = { bold: true, size: 14 };
    addTableDataToSheet(worksheet, exporter, combined, `ComponentTable_${section.title}`);
  }
}

/**
 * Generates a Construction Details worksheet with layer breakdowns for each component
 * @param {Object} worksheet - ExcelJS worksheet object
 * @param {Object} exporter - SpreadsheetExporterExcel instance (for unique table names)
 */
export function generateConstructionDetailsWorksheet(worksheet, exporter, system) {
  if (!gApp.proj()) return;

  for (const section of getComponentSections(system)) {
    if (section.items.length === 0) continue;
    const titleRow = worksheet.addRow(['', section.title]);
    titleRow.font = { bold: true, size: 16 };
    worksheet.addRow([]);
    for (const item of section.items) {
      const nameRow = worksheet.addRow(['', item.name.value]);
      nameRow.font = { size: 14 };
      addTableDataToSheet(worksheet, exporter, item.getConstructionDetailsData(), `Construction_${section.title}_${item.name.value}`);
    }
  }
}

/**
 * Generates a space loads worksheet with detailed load breakdowns for each space
 * @param {Object} worksheet - ExcelJS worksheet object
 * @param {Object} system - The system object containing load results
 */
export function generateSpaceLoadsWorksheet(worksheet, system) {
  let spaceResults = system.loadResults.getSpaceResultsData();

  // Generate a space loads table for each space
  for (let spaceResultIndex = 0; spaceResultIndex < spaceResults.length; spaceResultIndex++) {
    let spaceResult = spaceResults[spaceResultIndex];

    // Add space title
    let titleRow = worksheet.addRow(['', `Space Loads for ${spaceResult.spaceDesc}`]);
    titleRow.font = { bold: true, size: 14 };

    // Add design conditions header
    let designInfo = spaceResult.designInfo;
    let designInfoValueOpts = { precision: 0, includeSpace: true };
    let coolingOutdoorTempDB = designInfo.coolingOutdoorTempDB.makeValStr(designInfoValueOpts);
    let coolingOutdoorTempWB = designInfo.coolingOutdoorTempWB.makeValStr(designInfoValueOpts);
    let coolingIndoorTemp = designInfo.coolingIndoorTemp.makeValStr(designInfoValueOpts);
    let heatingOutdoorTempDB = designInfo.heatingOutdoorTempDB.makeValStr(designInfoValueOpts);
    let heatingIndoorTemp = designInfo.heatingIndoorTemp.makeValStr(designInfoValueOpts);
    let coolingDataStr = `Cooling data is at peak (${designInfo.coolingPeakTime}), OA DB/WB: ${coolingOutdoorTempDB} / ${coolingOutdoorTempWB}, Indoor: ${coolingIndoorTemp}`;
    let heatingDataStr = `Heating data is at design temp, OA DB: ${heatingOutdoorTempDB}, Indoor: ${heatingIndoorTemp}`;

    worksheet.addRow(['', coolingDataStr]);
    worksheet.addRow(['', heatingDataStr]);

    // Prepare table data
    let tableData = [];

    // Add data rows
    let loadUnits = spaceResult.loadUnits;
    let projectLoadUnits = Quantity.getConvertedUnits(spaceResult.loadUnits);
    let convertLoadVal = (value) => {
      return Math.round(Quantity.convertToProjectUnits(value, loadUnits).value);
    };
    for (let row of spaceResult.dataRows) {
      tableData.push([
        row.label,
        row.details !== null ? row.details.makeValStr({precision: 0}) : '-',
        row.coolingSensible !== null ? convertLoadVal(row.coolingSensible)  : '-',
        row.coolingLatent !== null ? convertLoadVal(row.coolingLatent) : '-',
        row.heatingSensible !== null ? convertLoadVal(row.heatingSensible) : '-',
        '-',
      ]);
    }

    // Add space totals row
    tableData.push([
      'Space Totals',
      '-',
      convertLoadVal(spaceResult.total.coolingSensible),
      convertLoadVal(spaceResult.total.coolingLatent),
      convertLoadVal(spaceResult.total.heatingSensible),
      '-',
    ]);

    // Add space quantity row
    tableData.push([
      'Space Quantity',
      spaceResult.quantity,
      '', '', '', ''
    ]);

    // Add zone totals row
    tableData.push([
      'Zone Totals',
      '-',
      convertLoadVal(spaceResult.zoneTotals.coolingSensible),
      convertLoadVal(spaceResult.zoneTotals.coolingLatent),
      convertLoadVal(spaceResult.zoneTotals.heatingSensible),
      '-',
    ]);

    // Create table with headers and data
    let tableStartRow = worksheet.rowCount + 1;
    let tableName = `SpaceLoads_${spaceResultIndex + 1}`;

    worksheet.addTable({
      name: tableName,
      ref: `B${tableStartRow}`,
      headerRow: true,
      style: {
        showRowStripes: true,
      },
      columns: [
        { name: 'Space Loads' },
        { name: 'Details' },
        { name: `Cooling Sensible (${Units.getLabel(projectLoadUnits)})` },
        { name: `Cooling Latent (${Units.getLabel(projectLoadUnits)})` },
        { name: `Heating Sensible (${Units.getLabel(projectLoadUnits)})` },
        { name: `Heating Latent (${Units.getLabel(projectLoadUnits)})` },
      ],
      rows: tableData
    });

    // Apply formatting to specific rows after table creation
    let dataRowCount = spaceResult.dataRows.length;
    let spaceTotalRowIndex = tableStartRow + 1 + dataRowCount;
    let quantityRowIndex = spaceTotalRowIndex + 1;
    let zoneTotalRowIndex = quantityRowIndex + 1;

    // Format space totals row
    let spaceTotalRow = worksheet.getRow(spaceTotalRowIndex);
    spaceTotalRow.font = { bold: true };

    // Format quantity row (bold)
    let quantityRow = worksheet.getRow(quantityRowIndex);
    quantityRow.font = { bold: true };

    // Format zone totals row (bold + blue background)
    let zoneTotalRow = worksheet.getRow(zoneTotalRowIndex);
    zoneTotalRow.font = { bold: true };

    // Right-align numeric columns (columns C through F)
    for (let rowIndex = tableStartRow + 1; rowIndex <= zoneTotalRowIndex; rowIndex++) {
      for (let col = 3; col <= 7; col++) {
        let cell = worksheet.getCell(rowIndex, col);
        cell.alignment = { horizontal: 'right' };
      }
    }

    // Add spacing between spaces
    if (spaceResultIndex < spaceResults.length - 1) {
      worksheet.addRow([]);
      worksheet.addRow([]);
    }
  }
}

// All sections included — used when nothing more targeted is available (e.g.
// the assistant's CSV results tool).
export const DEFAULT_EXCEL_EXPORT_OPTIONS = {
  excel: {
    includeCoilLoadSummaries: { value: true },
    includeSpaceLoadTables: { value: true },
    includeSystemInputs: { value: true },
    includeSpaceInputs: { value: true },
    includeComponentTables: { value: true },
    includeConstructionDetails: { value: true },
  },
};

/**
 * Builds (but does not download) the Excel workbook of system results.
 * @param {Object} system - The system object containing load results
 * @param {Object} exportOptions - Options controlling what data to include in the export
 * @returns {Promise<Object>} the populated SpreadsheetExporterExcel instance
 */
async function buildSystemResultsWorkbook(system, exportOptions) {
  // Dynamically import SpreadsheetExporter to avoid bloating the bundle
  const SpreadsheetExporter = await import('../Common/SpreadsheetExporter.js');

  // Replace spaces in project name with underscores
  let projName = gApp.proj().getName().replace(/ /g, "_");
  // Make a date string, like "2024-12-31"
  let dateStr = new Date().toLocaleDateString('en-CA');
  let fileName = `${projName}_${dateStr}.xlsx`
  let SpreadsheetExporterExcel = SpreadsheetExporter.SpreadsheetExporterExcel;
  let exporter = new SpreadsheetExporterExcel(fileName);
  let loadResults = system.loadResults;

  // Add header info to first worksheet
  let headerOpts = {
    systemName: system.name.value || 'Untitled System',
    projectName: gApp.proj().getName()
  };

  let excelOptions = exportOptions.excel;
  if (excelOptions.includeCoilLoadSummaries.value) {
    let outputSummarySections = [
      ...loadResults.getOutputSummary().allSections,
      ...loadResults.getZoneLoadsSummary().generalSection
    ];
    exporter.addSummaryDataPage("Summary", outputSummarySections, headerOpts);
  }
  if (excelOptions.includeSpaceLoadTables.value) {
    exporter.addCustomPage("Space Loads", (worksheet) => generateSpaceLoadsWorksheet(worksheet, system), headerOpts);
  }
  if (excelOptions.includeSystemInputs.value) {
    exporter.addSummaryDataPage("System Inputs", loadResults.getSystemInputSummary().allSections, headerOpts);
  }
  if (excelOptions.includeSpaceInputs.value) {
    exporter.addSummaryDataPage("Space Inputs", loadResults.getSpaceInputSummary(), headerOpts);
  }
  if (excelOptions.includeComponentTables.value) {
    exporter.addCustomPage("Component Tables", (worksheet, exp) => generateComponentTablesWorksheet(worksheet, exp, system), headerOpts);
  }
  if (excelOptions.includeConstructionDetails.value) {
    exporter.addCustomPage("Construction Details", (worksheet, exp) => generateConstructionDetailsWorksheet(worksheet, exp, system), headerOpts);
  }

  return exporter;
}

/**
 * Exports system results to an Excel file (triggers a browser download).
 * @param {Object} system - The system object containing load results
 * @param {Object} exportOptions - Options controlling what data to include in the export
 * @returns {Promise<void>}
 */
export async function exportResultsToExcel(system, exportOptions) {
  let exporter = await buildSystemResultsWorkbook(system, exportOptions);
  exporter.export();
}

/**
 * Builds the same data as exportResultsToExcel, but returns it as a plain-text
 * CSV blob (one section per worksheet) instead of downloading an .xlsx file —
 * meant for feeding system results to the AI assistant.
 * @param {Object} system - The system object containing load results
 * @returns {Promise<string>}
 */
export async function getSystemResultsCsv(system) {
  let exporter = await buildSystemResultsWorkbook(system, DEFAULT_EXCEL_EXPORT_OPTIONS);
  return workbookToCsv(exporter.workbook);
}
